import { Button } from '@warp-ds/react';
import cn from 'classnames';
import type { GeoHierarchyNode } from '../utils/destinationPicker';
import { sortedByHotelCount } from '../utils/destinationPicker';

type DestinationPickerBrowseViewProps = {
    isMobile?: boolean;
    hierarchy: GeoHierarchyNode[];
    activeContinentNode?: GeoHierarchyNode;
    onActiveContinentChange: (continent: string) => void;
    onCountrySelect: (country: GeoHierarchyNode) => void;
};

export const DestinationPickerBrowseView = ({
    isMobile = false,
    hierarchy,
    activeContinentNode,
    onActiveContinentChange,
    onCountrySelect,
}: DestinationPickerBrowseViewProps) => {
    const continents = sortedByHotelCount(hierarchy);
    const countries = sortedByHotelCount(activeContinentNode?.children ?? []);

    return (
        <div className={cn({ 'grid min-h-[360px] grid-cols-[180px_1fr]': !isMobile, 'min-h-0': isMobile })}>
            <div
                className={cn({
                    'border-r s-border p-8': !isMobile,
                    'overflow-x-auto border-b s-border': isMobile,
                })}>
                {isMobile ? (
                    <div role="tablist" aria-label="Verdensdeler" className="flex w-max px-16 ">
                        {continents.map((continent) => (
                            <button
                                key={continent.displayName}
                                type="button"
                                role="tab"
                                aria-selected={continent.displayName === activeContinentNode?.displayName}
                                className={cn('min-h-44 shrink-0 border-b-2 bg-transparent px-16 py-12 text-s font-bold', {
                                    's-border-primary s-text-link': continent.displayName === activeContinentNode?.displayName,
                                    'border-transparent s-text-subtle': continent.displayName !== activeContinentNode?.displayName,
                                })}
                                onClick={() => onActiveContinentChange(continent.displayName)}>
                                {continent.name}
                            </button>
                        ))}
                    </div>
                ) : (
                    continents.map((continent) => (
                        <Button
                            key={continent.displayName}
                            type="button"
                            quiet
                            fullWidth
                            className={cn('mb-4 justify-start text-left', {
                                's-bg-primary-subtle': continent.displayName === activeContinentNode?.displayName,
                            })}
                            onClick={() => onActiveContinentChange(continent.displayName)}>
                            {continent.name}
                        </Button>
                    ))
                )}
            </div>

            <div className={cn({ 'max-h-[360px] overflow-y-auto p-12': !isMobile })}>
                {countries.map((country) => {
                    const preview = (country.children ?? [])
                        .slice(0, 6)
                        .map((region) => region.name)
                        .join(', ');
                    const destinationCount = (country.children ?? []).reduce(
                        (total, region) => total + Math.max(region.children?.length ?? 0, 1),
                        0,
                    );

                    return (
                        <button
                            key={country.displayName}
                            type="button"
                            className={cn(
                                'flex w-full px-16 items-center justify-between text-left focusable s-border s-bg hover:s-bg-hover',
                                {
                                    'mb-8 rounded-8 py-12': !isMobile,
                                    'border-b py-14': isMobile,
                                },
                            )}
                            onClick={() => onCountrySelect(country)}>
                            <span className="min-w-0">
                                <span className={cn('block', { 'text-m': isMobile, 'font-bold': !isMobile })}>{country.name}</span>
                                {isMobile ? (
                                    <span className="block truncate text-xs s-text-subtle">{destinationCount} reisemål</span>
                                ) : (
                                    preview && <span className="block truncate text-s s-text-subtle">{preview}</span>
                                )}
                            </span>
                            <span className="flex shrink-0 items-center gap-4 text-s s-text-subtle">
                                {!isMobile && `${destinationCount} reisemål`}
                                <w-icon name="ChevronRight" size="small" aria-hidden className={cn({ 's-text-link': isMobile })} />
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};
